import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset Your Password — Cailève" },
      { name: "description", content: "Set a new password for your Cailève account." },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase parses the recovery token from the URL hash automatically and
    // emits a PASSWORD_RECOVERY event. We just need a valid session for
    // updateUser to succeed.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password || password.length < 6) {
      setError("Please enter a password of at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    const { error: upErr } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (upErr) {
      setError(upErr.message);
      return;
    }
    setDone(true);
    setTimeout(() => {
      void supabase.auth.signOut().finally(() => navigate({ to: "/login" }));
    }, 3000);
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "var(--caileve-bg)" }}
    >
      <div className="w-full max-w-md">
        <h1
          className="text-center text-4xl mb-8"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: "var(--caileve-ink)",
          }}
        >
          Reset Your Password
        </h1>

        <div className="bg-[var(--caileve-bg)] border border-[var(--caileve-soft)] p-8 shadow-sm">
          {done ? (
            <p
              className="text-center text-lg italic leading-relaxed"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "var(--caileve-ink)",
              }}
            >
              You're back. Your collection is waiting.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <label
                  htmlFor="new-pw"
                  className="block text-xs uppercase tracking-[0.25em]"
                  style={{ color: "var(--caileve-lilac)" }}
                >
                  New Password
                </label>
                <input
                  id="new-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[var(--caileve-soft)] bg-[var(--caileve-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--caileve-lilac)]"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="confirm-pw"
                  className="block text-xs uppercase tracking-[0.25em]"
                  style={{ color: "var(--caileve-lilac)" }}
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-pw"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full border border-[var(--caileve-soft)] bg-[var(--caileve-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--caileve-lilac)]"
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <p className="text-sm" style={{ color: "var(--caileve-copper)" }}>
                  {error}
                </p>
              )}
              {!ready && (
                <p className="text-xs italic" style={{ color: "var(--caileve-muted)" }}>
                  Verifying your reset link…
                </p>
              )}
              <button
                type="submit"
                disabled={busy || !ready}
                className="w-full py-3 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)] transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--caileve-lilac)" }}
              >
                {busy ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}